import React, { useCallback, useRef, useState } from 'react'
import { AccessTime, ExpandMore } from '@mui/icons-material'

import { useGlobalFilter } from '../contexts/GlobalFilterContext'
import { useEffectiveTimestampMode } from '../contexts/PageTimestampModeDefault'
import { useEscapeKey } from '../hooks/useEscapeKey'
import type { TimeFilterPreset } from '../types/filterTypes'
import type { TimestampMode, TimezoneMode } from '../types/filterTypes'
import { TIME_FILTER_PRESET_LABELS } from '../types/filterTypes'
import { resolvedTimeFilterLabel } from '../utils/filterHelpers'

import CustomDateRangePicker from './CustomDateRangePicker'

type TimeRangeFilterControlProps = {
  className?: string
  disabled?: boolean
}

// Preset menu is grouped by tier so the list scans by magnitude at a glance
// (all minute-range windows together, then hours, then days) rather than as
// one 9-row wall. Tiers are hand-authored, not derived from the preset key,
// so a future preset with a non-standard label (e.g. 'Last quarter') can
// live in its own group without regex fragility. Keep in sync with the
// `TimeFilterPreset` union in filterTypes.ts.
const PRESET_TIERS: { label: string; presets: TimeFilterPreset[] }[] = [
  { label: 'Minutes', presets: ['last_30m'] },
  {
    label: 'Hours',
    presets: ['last_1h', 'last_2h', 'last_4h', 'last_12h', 'last_24h'],
  },
  { label: 'Days', presets: ['last_7d', 'last_30d', 'last_90d'] },
]

// @purpose Control for selecting a global time-range filter with presets, custom range, and interval shifting
const TimeRangeFilterControl = ({
  className = '',
  disabled = false,
}: TimeRangeFilterControlProps): React.JSX.Element => {
  const {
    filters,
    setTimeFilter,
    hasActiveTimeFilter,
    setTimestampMode,
    setTimezoneMode,
  } = useGlobalFilter()
  // PENG-14090 — the "Filter by" toggle highlights the EFFECTIVE mode (user
  // explicit pick → page default → global fallback), not the raw stored one,
  // so when the user is on a page that registered its own default (e.g. the
  // Component Catalog's 'first_seen') the toggle matches what the catalog is
  // actually sending on the wire — until the user clicks either value, which
  // becomes an explicit pick and takes over everywhere from then on.
  const effectiveTimestampMode = useEffectiveTimestampMode()
  const [isOpen, setIsOpen] = useState(false)
  const [showCustom, setShowCustom] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // PENG-13715: dismiss with Escape. Mirrors backdrop-click behaviour by
  // closing everything — both the preset menu and the nested custom-range
  // dialog — in one press. The filter isn't mutated on close, so the
  // previously-applied range is preserved (matches Cancel in the picker).
  const closeDropdown = useCallback(() => {
    setIsOpen(false)
    setShowCustom(false)
  }, [])
  useEscapeKey(isOpen, closeDropdown)

  const timeFilter = filters.timeFilter
  const isPreset = timeFilter.mode === 'preset'
  const activePreset = timeFilter.mode === 'preset' ? timeFilter.preset : null
  const currentShift =
    timeFilter.mode === 'preset' ? (timeFilter.shift ?? 0) : 0

  const handleToggle = () => {
    if (disabled) return
    if (isOpen) {
      setIsOpen(false)
      setShowCustom(false)
    } else {
      setIsOpen(true)
    }
  }

  const handlePresetSelect = (preset: TimeFilterPreset) => {
    setTimeFilter({ mode: 'preset', preset })
    setIsOpen(false)
    setShowCustom(false)
  }

  const handleCustomApply = (startUtc: string, endUtc: string) => {
    setTimeFilter({ mode: 'custom', start: startUtc, end: endUtc })
    setIsOpen(false)
    setShowCustom(false)
  }

  const MAX_SHIFT = 365

  const handleShiftBack = () => {
    if (!activePreset || currentShift >= MAX_SHIFT) return
    setTimeFilter({
      mode: 'preset',
      preset: activePreset,
      shift: currentShift + 1,
    })
  }

  const handleShiftForward = () => {
    if (!activePreset || currentShift <= 0) return
    setTimeFilter({
      mode: 'preset',
      preset: activePreset,
      shift: currentShift - 1,
    })
  }

  const getButtonClassName = () => {
    // On-dark treatment — this control lives only on the Deep Blue header bar.
    if (disabled) {
      return 'opacity-50 bg-header-pill border-header-line text-header-muted'
    }
    if (hasActiveTimeFilter) {
      return 'bg-blue-500/20 border-blue-400 text-white'
    }
    return 'bg-header-pill border-header-line text-white hover:bg-header-line'
  }

  // On-dark shift buttons — the control lives only on the Deep Blue header bar.
  const shiftButtonClass =
    'flex items-center justify-center w-8 h-8 rounded border text-sm font-bold transition-colors ' +
    'bg-header-pill border-header-line text-white hover:bg-header-line ' +
    'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-header-pill'

  const activeLabel = hasActiveTimeFilter
    ? resolvedTimeFilterLabel(filters.timeFilter, filters.timezoneMode)
    : null

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {/* Backward shift button — only shown when a preset is active */}
      {isPreset && (
        <button
          onClick={handleShiftBack}
          disabled={disabled}
          className={shiftButtonClass}
          aria-label={`Shift time window back by ${activePreset ? TIME_FILTER_PRESET_LABELS[activePreset] : ''}`}
          title={`Shift back one interval (${activePreset ? TIME_FILTER_PRESET_LABELS[activePreset] : ''})`}
          data-testid="time-shift-back"
        >
          «
        </button>
      )}

      {/* Trigger Button */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={handleToggle}
          className={`flex items-center gap-2 px-4 py-1 border rounded-lg transition-colors ${getButtonClassName()}`}
          aria-label="Time Range Filter"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          data-testid="time-range-filter-trigger"
          disabled={disabled}
        >
          <AccessTime sx={{ fontSize: 20 }} />
          <div className="text-left">
            <div className="font-medium text-sm">Time Range</div>
            <div className="text-xs text-header-muted max-w-[140px] truncate">
              {activeLabel ?? 'All time'}
            </div>
          </div>
          <ExpandMore
            sx={{ fontSize: 16 }}
            className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Dropdown */}
        {isOpen && !disabled && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => {
                setIsOpen(false)
                setShowCustom(false)
              }}
            />

            <div className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20">
              {!showCustom ? (
                <>
                  {/* Preset list — grouped by tier (minutes / hours / days)
                      so the menu scans by magnitude. Tier headers use
                      role="presentation" so screen readers reading the
                      listbox skip over them and just enumerate options; the
                      grouping is a sighted affordance. */}
                  <ul
                    role="listbox"
                    aria-label="Select time range"
                    className="py-1"
                  >
                    {PRESET_TIERS.map((tier, tierIndex) => (
                      <React.Fragment key={tier.label}>
                        {tierIndex > 0 && (
                          <li
                            role="separator"
                            className="my-1 border-t border-gray-100 dark:border-gray-700"
                          />
                        )}
                        <li
                          role="presentation"
                          className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
                        >
                          {tier.label}
                        </li>
                        {tier.presets.map(preset => {
                          const isActive =
                            filters.timeFilter.mode === 'preset' &&
                            filters.timeFilter.preset === preset
                          return (
                            <li key={preset} role="none">
                              <button
                                role="option"
                                aria-selected={isActive}
                                onClick={() => handlePresetSelect(preset)}
                                className={`w-full text-left px-4 py-2 text-sm transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 ${
                                  isActive
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium'
                                    : 'text-gray-900 dark:text-gray-100'
                                }`}
                              >
                                {TIME_FILTER_PRESET_LABELS[preset]}
                              </button>
                            </li>
                          )
                        })}
                      </React.Fragment>
                    ))}

                    {/* Divider before custom option */}
                    <li
                      role="separator"
                      className="my-1 border-t border-gray-100 dark:border-gray-700"
                    />

                    <li role="none">
                      <button
                        role="option"
                        aria-selected={filters.timeFilter.mode === 'custom'}
                        onClick={() => setShowCustom(true)}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 ${
                          filters.timeFilter.mode === 'custom'
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium'
                            : 'text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        Custom absolute range…
                      </button>
                    </li>
                  </ul>

                  {/* Filter by timestamp type */}
                  <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                      Filter by
                    </p>
                    <div className="flex rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden text-xs">
                      {(
                        [
                          ['last_seen', 'Most recent activity'],
                          ['first_seen', 'Earliest activity'],
                        ] as [TimestampMode, string][]
                      ).map(([mode, label]) => (
                        <button
                          key={mode}
                          onClick={() => setTimestampMode(mode)}
                          className={`flex-1 py-1.5 transition-colors ${
                            effectiveTimestampMode === mode
                              ? 'bg-blue-600 text-white font-medium'
                              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Timezone */}
                  <div className="px-4 pb-3">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                      Timezone
                    </p>
                    <div className="flex rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden text-xs">
                      {(
                        [
                          ['utc', 'UTC'],
                          ['local', 'Local'],
                        ] as [TimezoneMode, string][]
                      ).map(([mode, label]) => (
                        <button
                          key={mode}
                          onClick={() => setTimezoneMode(mode)}
                          className={`flex-1 py-1.5 transition-colors ${
                            filters.timezoneMode === mode
                              ? 'bg-blue-600 text-white font-medium'
                              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                /* Custom absolute date range picker */
                <CustomDateRangePicker
                  onApply={handleCustomApply}
                  onCancel={() => setShowCustom(false)}
                  initialStart={
                    filters.timeFilter.mode === 'custom'
                      ? filters.timeFilter.start
                      : undefined
                  }
                  initialEnd={
                    filters.timeFilter.mode === 'custom'
                      ? filters.timeFilter.end
                      : undefined
                  }
                />
              )}
            </div>
          </>
        )}
      </div>

      {/* Forward shift button — only shown when a preset is active; disabled when already at current time */}
      {isPreset && (
        <button
          onClick={handleShiftForward}
          disabled={currentShift === 0 || disabled}
          className={shiftButtonClass}
          aria-label="Shift time window forward one interval"
          title={
            currentShift === 0
              ? 'Cannot shift forward — window already ends at current time'
              : 'Shift forward one interval'
          }
          data-testid="time-shift-forward"
        >
          »
        </button>
      )}

      {/* Screen reader live region */}
      <div role="status" aria-live="polite" className="sr-only">
        {hasActiveTimeFilter
          ? `Time filter active: ${resolvedTimeFilterLabel(filters.timeFilter, filters.timezoneMode)}`
          : ''}
      </div>
    </div>
  )
}

export default TimeRangeFilterControl
